import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineEnvelope,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineShieldCheck,
  HiOutlineUserGroup,
  HiOutlineNoSymbol,
  HiOutlineBuildingOffice2,
  HiOutlineMagnifyingGlass,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import Modal from "../../../components/ui/modal/Modal";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";

import UserForm from "../../../components/forms/user/UserForm";

import {
  disableUser,
  getUsers,
  resendSetupInvite,
  setUserFilters,
} from "../../../redux/features/users/userSlice";

import {
  UserRoleBadge,
  UserStatusBadge,
} from "../../../components/users/UserBadges";

import usePermissions from "../../../hooks/usePermissions";

import "./UsersPage.css";

const ROLE_OPTIONS = [
  {
    value: "",
    label: "Alle rollen",
  },

  {
    value: "POWER_ADMIN",
    label: "Platform admin",
  },

  {
    value: "POWER_ORGANIZATION_ADMIN",
    label: "Organisatie admin",
  },

  {
    value: "POWER_INTERNAL_COACH",
    label: "Interne coach",
  },

  {
    value: "POWER_EXTERNAL_COACH",
    label: "Externe coach",
  },
];

export default function UsersPage() {
  const dispatch = useDispatch();

  const { can, user } = usePermissions();

  const { users, pagination, filters, isLoading, isInviting, error } =
    useSelector((state) => state.users);

  const isPowerAdmin = user?.role === "POWER_ADMIN";

  /**
   * =====================================================
   * LOCAL STATE
   * =====================================================
   */

  const [page, setPage] = useState(1);

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [openEditModal, setOpenEditModal] = useState(false);

  const [selectedEditUser, setSelectedEditUser] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,

    title: "",

    message: "",

    confirmText: "Bevestigen",

    variant: "danger",

    onConfirm: null,
  });

  /**
   * =====================================================
   * LOAD USERS
   * =====================================================
   */

  useEffect(() => {
    dispatch(
      getUsers({
        page,

        limit: 20,

        ...filters,
      }),
    );
  }, [
    dispatch,
    page,
    filters.search,
    filters.role,
    filters.status,
    filters.auth_provider,
    filters.organization,
  ]);

  /**
   * =====================================================
   * CLOSE CONFIRM
   * =====================================================
   */

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,

      title: "",

      message: "",

      confirmText: "Bevestigen",

      variant: "danger",

      onConfirm: null,
    });
  };

  /**
   * =====================================================
   * FILTER CHANGE
   * =====================================================
   */

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    setPage(1);

    dispatch(
      setUserFilters({
        [name]: value,
      }),
    );
  };

  /**
   * =====================================================
   * EDIT
   * =====================================================
   */

  const openEdit = (user) => {
    setSelectedEditUser(user);

    setOpenEditModal(true);
  };

  /**
   * =====================================================
   * DISABLE
   * =====================================================
   */

  const openDisableConfirm = (user) => {
    setConfirmModal({
      isOpen: true,

      title: "Gebruiker uitschakelen?",

      message: `${user.first_name} ${user.last_name} kan daarna niet meer inloggen.`,

      confirmText: "Uitschakelen",

      variant: "danger",

      onConfirm: async () => {
        await dispatch(disableUser(user._id)).unwrap();

        closeConfirmModal();
      },
    });
  };

  /**
   * =====================================================
   * RESEND INVITE
   * =====================================================
   */

  const handleResendInvite = async (user) => {
    try {
      await dispatch(resendSetupInvite(user._id)).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * =====================================================
   * PAGINATION
   * =====================================================
   */

  const handlePreviousPage = () => {
    if (page <= 1) return;

    setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (page >= pagination.pages) return;

    setPage((prev) => prev + 1);
  };

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  if (isLoading && users.length === 0) {
    return <PageLoader title="Gebruikers laden..." />;
  }

  return (
    <div className="users-page">
      <PageHeader
        eyebrow="POWER"
        title="Gebruikersbeheer"
        description="Beheer gebruikers, coaches en organisaties."
        actions={
          can("user.create") && (
            <button
              className="users-page__create-button"
              onClick={() => setOpenCreateModal(true)}
            >
              <HiOutlinePlus />

              <span>Nieuwe gebruiker</span>
            </button>
          )
        }
      />

      {error && <div className="users-page__error">{error}</div>}

      {/* STATS */}
      <PageCard className="users-page__stats-card">
        <div className="users-page__stats">
          <div className="users-page__stats-item">
            <span>Totaal gebruikers</span>

            <strong>{pagination.total || 0}</strong>
          </div>

          <div className="users-page__stats-item">
            <span>Pagina</span>

            <strong>
              {pagination.page || 1} / {pagination.pages || 1}
            </strong>
          </div>
        </div>
      </PageCard>

      {/* FILTERS */}
      <PageCard className="users-page__filters-card">
        <div className="users-page__filters">
          <div className="users-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Zoeken op naam of e-mail..."
            />
          </div>

          <select
            name="role"
            value={filters.role}
            onChange={handleFilterChange}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">Alle statussen</option>

            <option value="INVITED">Uitgenodigd</option>

            <option value="ACTIVE">Actief</option>

            <option value="BLOCKED">Geblokkeerd</option>

            <option value="DISABLED">Uitgeschakeld</option>
          </select>

          <select
            name="auth_provider"
            value={filters.auth_provider}
            onChange={handleFilterChange}
          >
            <option value="">Alle providers</option>

            <option value="core">Core</option>

            <option value="local">Local</option>
          </select>
        </div>
      </PageCard>

      {/* EMPTY */}
      {users.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineUserGroup />}
          title="Geen gebruikers gevonden"
          description="Er zijn geen gebruikers die overeenkomen met deze filters."
        />
      ) : (
        <>
          {/* TABLE */}
          <PageCard className="users-page__table-card">
            <div className="users-page__table-wrapper">
              <table className="users-page__table">
                <thead>
                  <tr>
                    <th>Naam</th>

                    <th>E-mail</th>

                    <th>Rol</th>

                    <th>Status</th>

                    <th>Organisatie</th>

                    <th>Provider</th>

                    <th>MFA</th>

                    <th>Laatste login</th>

                    <th>Acties</th>
                  </tr>
                </thead>

                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <strong>
                          {user.first_name} {user.last_name}
                        </strong>
                      </td>

                      <td>{user.email}</td>

                      <td>
                        <UserRoleBadge role={user.role} />
                      </td>

                      <td>
                        <UserStatusBadge status={user.status} />
                      </td>

                      <td>
                        <div className="users-page__organization">
                          <HiOutlineBuildingOffice2 />

                          <span>
                            {user.organization?.name || "Geen organisatie"}
                          </span>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`users-page__provider users-page__provider--${user.auth_provider}`}
                        >
                          {user.auth_provider}
                        </span>
                      </td>

                      <td>{user.mfa_enabled ? "Actief" : "Niet actief"}</td>

                      <td>
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString(
                              "nl-BE",
                            )
                          : "-"}
                      </td>

                      <td>
                        <div className="users-page__actions">
                          {can("user.update") && (
                            <button
                              onClick={() => openEdit(user)}
                              title="Wijzigen"
                            >
                              <HiOutlinePencilSquare />
                            </button>
                          )}

                          {can("user.permissions") && isPowerAdmin && (
                            <button title="Rechten">
                              <HiOutlineShieldCheck />
                            </button>
                          )}

                          {can("user.invite") &&
                            [
                              "POWER_EXTERNAL_COACH",

                              "POWER_ORGANIZATION_ADMIN",
                            ].includes(user.role) &&
                            user.status === "INVITED" && (
                              <button
                                disabled={isInviting}
                                onClick={() => handleResendInvite(user)}
                                title="Uitnodiging opnieuw verzenden"
                              >
                                <HiOutlineEnvelope />
                              </button>
                            )}

                          {can("user.disable") &&
                            user.status !== "DISABLED" && (
                              <button
                                className="users-page__danger"
                                onClick={() => openDisableConfirm(user)}
                                title="Uitschakelen"
                              >
                                <HiOutlineNoSymbol />
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </PageCard>

          {/* PAGINATION */}
          <div className="users-page__pagination">
            <button disabled={page <= 1} onClick={handlePreviousPage}>
              <HiOutlineChevronLeft />

              <span>Vorige</span>
            </button>

            <div className="users-page__pagination-info">
              Pagina {page} van {pagination.pages || 1}
            </div>

            <button
              disabled={page >= pagination.pages}
              onClick={handleNextPage}
            >
              <span>Volgende</span>

              <HiOutlineChevronRight />
            </button>
          </div>
        </>
      )}

      {/* CREATE */}
      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Nieuwe gebruiker"
        description="Maak een nieuwe POWER gebruiker aan."
      >
        <UserForm
          mode="create"
          onSuccess={() => {
            setOpenCreateModal(false);
          }}
          onCancel={() => setOpenCreateModal(false)}
        />
      </Modal>

      {/* EDIT */}
      <Modal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title="Gebruiker wijzigen"
        description="Wijzig de basisgegevens van deze gebruiker."
      >
        <UserForm
          mode="edit"
          initialData={selectedEditUser}
          onSuccess={() => {
            setOpenEditModal(false);

            setSelectedEditUser(null);
          }}
          onCancel={() => setOpenEditModal(false)}
        />
      </Modal>

      {/* CONFIRM */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
}
